import { useState, useRef, useMemo, useEffect } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ChatParticipant } from "@/lib/api/chats";
import { Input } from "@/components/ui/input";
import { Avatar } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface GroupChatParticipantsSidebarProps {
  participants: ChatParticipant[];
}

// Helper to get participant name (same as ChatDetail)
const getParticipantName = (participant: ChatParticipant | undefined) => {
  if (!participant) return "Unknown";

  if (participant.User) {
    return participant.User.name || participant.User.email || "User";
  }

  if (participant.Customer) {
    return participant.Customer.long_name || "Customer";
  }

  return participant.sms_number || "Unknown";
};

// Helper to get participant type (same as ChatDetail)
const getParticipantType = (participant: ChatParticipant | undefined) => {
  if (!participant) return "unknown";
  if (participant.User) return "user";
  return "customer";
};

// Helper to get searchable text from participant
const getSearchableText = (participant: ChatParticipant): string => {
  const name = getParticipantName(participant);
  const email = participant.User?.email || "";
  const phone = participant.sms_number || "";
  const customerName = participant.Customer?.long_name || "";
  return `${name} ${email} ${phone} ${customerName}`.toLowerCase();
};

export function GroupChatParticipantsSidebar({
  participants,
}: GroupChatParticipantsSidebarProps) {
  const [search, setSearch] = useState("");
  const parentRef = useRef<HTMLDivElement>(null);

  // Filter participants based on search query
  const filteredParticipants = useMemo(() => {
    if (!search?.trim()) return participants;
    const searchLower = search.toLowerCase().trim();
    return participants.filter((participant) =>
      getSearchableText(participant).includes(searchLower)
    );
  }, [participants, search]);

  const virtualizer = useVirtualizer({
    count: filteredParticipants.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60, // Approximate height of each participant item
    overscan: 10,
  });

  // Force recalculate when participants change
  useEffect(() => {
    virtualizer.measure();
  }, [participants.length, virtualizer]);

  return (
    <div className="flex flex-col h-full border rounded-lg overflow-hidden">
      {/* Header with search */}
      <div className="p-4 border-b bg-muted/50">
        <h2 className="font-semibold mb-2">Participants</h2>
        <Input
          placeholder="Search participants..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full"
        />
      </div>

      {/* Virtualized participant list */}
      <div
        ref={parentRef}
        className="flex-1 overflow-auto"
        style={{ height: "100%" }}
      >
        {filteredParticipants.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            {search ? "No participants found" : "No participants"}
          </div>
        ) : (
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const participant = filteredParticipants[virtualItem.index];
              const participantType = getParticipantType(participant);
              const participantName = getParticipantName(participant);

              return (
                <div
                  key={participant.id}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                  className="px-4 py-3 border-b hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {participantName}
                      </p>
                      <p className="text-xs text-muted-foreground capitalize">
                        {participantType}
                        {participant.User?.email && (
                          <span className="ml-1">• {participant.User.email}</span>
                        )}
                        {participant.sms_number && (
                          <span className="ml-1">• {participant.sms_number}</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

